import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'Button';
import { Container } from 'Container';
import { Footer } from 'Footer';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { Text } from 'Text';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { useSiteMetadata } from 'SearchEngineOptimization/useSiteMetadata';
import { getRelevantCohorts } from 'Users';
import { browseCoursesApi } from '../browseCoursesApi';
import { BrowseCoursesContent } from './BrowseCoursesContent';
import '../locales/browseCourses/browseCourses-en.json';

function useCoursesPageHeader(title: string) {
    const AppHeaderViewModel = useAppHeaderViewModel();
    const SiteMetadata = useSiteMetadata();

    useEffect(() => {
        AppHeaderViewModel.setBodyBackground('slate-grey-100-alt');
        AppHeaderViewModel.showAlternateHomeButton = false;
        AppHeaderViewModel.setTitleHTML(title);
        SiteMetadata.updateHeaderMetadata();
    }, [AppHeaderViewModel, SiteMetadata, title]);
}

const LoadError: FC<{ onRetry: () => void }> = ({ onRetry }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });

    return (
        <div className="flex flex-col items-center gap-5 py-10">
            <Text as="p" weight="semibold" color="black" className="text-center">
                {t('somethingWentWrong')}
            </Text>
            <Button color="black" onPress={onRetry}>
                {t('retry')}
            </Button>
        </div>
    );
};

export function BrowseCoursesPage() {
    const { t, i18n } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
    const currentUser = useCurrentUser();
    useCoursesPageHeader(t('coursesTitle'));

    const cohortIds = useMemo(
        () => (currentUser ? getRelevantCohorts(currentUser).map(cohort => cohort.id) : null),
        [currentUser],
    );

    // Refetch on each visit, so progress is fresh after a lesson
    const {
        data: streams,
        isError,
        refetch,
    } = browseCoursesApi.useGetBrowseStreamsQuery(
        { cohortIds, localeCode: i18n.language },
        { refetchOnMountOrArgChange: true },
    );

    // The `fixed` spinner is full width with no left offset, so it must sit outside the padded Container
    // to center on the screen
    if (!streams && !isError) return <FrontRoyalSpinner className="fixed" />;

    return (
        <div className="flex min-h-full flex-col bg-slate-grey-100-alt">
            <Container className="flex-1 py-5 sm:py-7.5">
                {streams ? (
                    <BrowseCoursesContent streams={streams} currentUser={currentUser} />
                ) : (
                    <LoadError onRetry={refetch} />
                )}
            </Container>
            {streams && <Footer />}
        </div>
    );
}
